import { useState } from "react";
import { X, Send, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";

const USNursesInterestForm = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    contactMethod: "",
    examType: "",
    examTimeline: "",
    languageLevel: "",
    additionalInfo: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const examOptions = [
    "TOEFL",
    "IELTS",
    "OET",
    "Other (Please specify)",
  ];

  const timelineOptions = [
    "Within 3 months",
    "3-6 months",
    "6-12 months",
    "More than a year from now",
    "I'm not sure yet",
  ];

  const levelOptions = [
    { value: "Beginner", label: t("usNurses.form.levels.beginner") },
    { value: "Intermediate", label: t("usNurses.form.levels.intermediate") },
    { value: "Advanced", label: t("usNurses.form.levels.advanced") },
  ];

  const contactMethodOptions = ["Email", "Phone", "Zoom"];

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const MAX_PHONE_LENGTH = 20;
  const PHONE_ERROR_MSG =
    t("usNurses.form.errors.invalidPhoneWithCountryCode") ||
    "Please enter a valid phone number with country code (e.g., +1 for US, +44 for UK)";

  const validateEmail = (emailValue) => EMAIL_REGEX.test(emailValue);

  const validatePhoneNumber = (phoneValue, forceValidation = false) => {
    if (!phoneValue?.trim()) {
      setPhoneError("");
      return false;
    }
    if (!forceValidation && phoneValue.trim().length < 7) {
      setPhoneError("");
      return false;
    }
    try {
      if (isValidPhoneNumber(phoneValue)) {
        setPhoneError("");
        return true;
      }
      setPhoneError(PHONE_ERROR_MSG);
      return false;
    } catch {
      setPhoneError(PHONE_ERROR_MSG);
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "email" && emailError) setEmailError("");
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    if (!value) {
      setFormData((prev) => ({ ...prev, phone: "" }));
      setPhoneError("");
      return;
    }
    if (value.startsWith("+")) {
      value = "+" + value.slice(1).replace(/[^\d\s\-()]/g, "");
    } else {
      value = value.replace(/[^\d\s\-()+]/g, "");
      if (value && !value.startsWith("+") && /^\d/.test(value))
        value = "+" + value;
    }
    if (value.length > MAX_PHONE_LENGTH)
      value = value.slice(0, MAX_PHONE_LENGTH);
    setFormData((prev) => ({ ...prev, phone: value }));
    validatePhoneNumber(value);
  };

  const handleEmailBlur = () => {
    setEmailError(
      formData.email && !validateEmail(formData.email)
        ? t("usNurses.form.errors.invalidEmail")
        : "",
    );
  };

  const handlePhoneBlur = () => {
    if (formData.phone.trim()) validatePhoneNumber(formData.phone, true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(formData.email)) {
      setEmailError(t("usNurses.form.errors.invalidEmail"));
      return;
    }

    // Validate phone number before submission
    if (!validatePhoneNumber(formData.phone, true)) {
      toast.error(PHONE_ERROR_MSG);
      return;
    }

    setIsSubmitting(true);

    try {
      // Parse and format the phone number properly
      let formattedPhone = formData.phone.trim();
      try {
        if (isValidPhoneNumber(formData.phone)) {
          const phoneNumber = parsePhoneNumber(formData.phone);
          // Format as international number
          formattedPhone = phoneNumber.formatInternational();
        }
      } catch (error) {
        // If parsing fails, use the original value
        console.warn("Could not parse phone number, using original:", error);
      }

      const submissionData = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formattedPhone,
        contactMethod: formData.contactMethod,
        examType: formData.examType,
        timeline: formData.examTimeline,
        languageLevel: formData.languageLevel,
        additionalInfo: formData.additionalInfo || "",
        formType: "US Nurses",
      };

      // Submit to Google Apps Script
      await fetch(
        "https://script.google.com/macros/s/AKfycbwQGrRWH0o1VuDV0v__gY0dnVdAJ3vnCvz4DzTU_J3r2RJH9SroDDfBRv7xhKrpGIKDFw/exec",
        {
          method: "POST",
          mode: "no-cors", // Required for Google Apps Script
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submissionData),
        },
      );

      // Close form and reset data
      onClose();
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        contactMethod: "",
        examType: "",
        examTimeline: "",
        languageLevel: "",
        additionalInfo: "",
      });
      setEmailError("");
      setPhoneError("");

      // Redirect to thank you page with source information
      navigate("/thank-you", { state: { from: "/exam-prep/nurses" } });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        t("usNurses.form.errors.submitError") ||
          "There was an error submitting the form. Please try again or contact us directly.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputBaseClass =
    "w-full rounded-xl border-2 px-4 py-3 text-[#042F0C] transition-all focus:outline-none focus:ring-2";
  const inputValidClass =
    "border-[#14B82C]/30 focus:border-[#14B82C] focus:ring-[#14B82C]/20";
  const inputErrorClass =
    "border-red-500 focus:border-red-500 focus:ring-red-500/20";
  const selectClass =
    "w-full appearance-none rounded-xl border-2 border-[#14B82C]/30 bg-white px-4 py-3 pr-12 text-[#042F0C] transition-all focus:border-[#14B82C] focus:outline-none focus:ring-2 focus:ring-[#14B82C]/20";

  return (
    <>
      {/* Form Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="relative mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-gradient-to-br from-[#F0FDF1] to-[#E6FDE9] p-8 shadow-2xl">
            <button
              onClick={onClose}
              className="absolute right-6 top-6 flex items-center justify-center rounded-full bg-white p-2 text-gray-600 shadow-lg transition-all hover:bg-gray-50 hover:shadow-xl"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Header Section */}
            <div className="mb-8 text-center">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-[#14B82C] p-4 shadow-lg">
                  <Send className="h-8 w-8 text-white" />
                </div>
              </div>
              <h2 className="mb-4 text-4xl font-bold text-[#042F0C]">
                {t("usNurses.form.title")}
              </h2>
              <p className="text-xl leading-relaxed text-[#3d3d3d]">
                {t("usNurses.form.description")}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Section 1: Contact Information */}
              <div className="rounded-2xl border border-[#14B82C]/20 bg-white p-8 shadow-lg">
                <div className="mb-6 flex items-center gap-3">
                  <div className="rounded-full bg-[#FFBF00] p-2">
                    <span className="text-lg font-bold text-[#042F0C]">1</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#042F0C]">
                    {t("usNurses.form.section1")}
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      {t("usNurses.form.fullName")} *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className={`${inputBaseClass} ${inputValidClass}`}
                      placeholder={t("usNurses.form.enterFullName")}
                    />
                  </div>

                  <div>
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      {t("usNurses.form.emailAddress")} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleEmailBlur}
                      required
                      className={`${inputBaseClass} ${emailError ? inputErrorClass : inputValidClass}`}
                      placeholder={t("usNurses.form.enterEmail")}
                    />
                    {emailError && (
                      <p className="mt-2 text-sm text-red-600">{emailError}</p>
                    )}
                  </div>

                  <div>
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      {t("usNurses.form.phoneNumber")} *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      onBlur={handlePhoneBlur}
                      required
                      className={`${inputBaseClass} ${phoneError ? inputErrorClass : inputValidClass}`}
                      placeholder="+1 555-123-4567"
                      maxLength={MAX_PHONE_LENGTH}
                    />
                    {phoneError && (
                      <p className="mt-2 text-sm text-red-600">{phoneError}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {t("usNurses.form.phoneHelper") ||
                        "Include country code (e.g., +1 for US, +44 for UK)"}
                    </p>
                  </div>

                  <div>
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      {t("usNurses.form.contactMethod")} *
                    </label>
                    <div className="relative">
                      <select
                        name="contactMethod"
                        value={formData.contactMethod}
                        onChange={handleInputChange}
                        required
                        className={selectClass}
                      >
                        <option value="">
                          {t("usNurses.form.selectContactMethod")}
                        </option>
                        {contactMethodOptions.map((method) => (
                          <option key={method} value={method}>
                            {method}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#3d3d3d]" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Exam Information */}
              <div className="rounded-2xl border border-[#14B82C]/20 bg-white p-8 shadow-lg">
                <div className="mb-6 flex items-center gap-3">
                  <div className="rounded-full bg-[#FFBF00] p-2">
                    <span className="text-lg font-bold text-[#042F0C]">2</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#042F0C]">
                    {t("usNurses.form.section2")}
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      {t("usNurses.form.examType")} *
                    </label>
                    <div className="relative">
                      <select
                        name="examType"
                        value={formData.examType}
                        onChange={handleInputChange}
                        required
                        className={selectClass}
                      >
                        <option value="">
                          {t("usNurses.form.selectExamType")}
                        </option>
                        {examOptions.map((exam) => (
                          <option key={exam} value={exam}>
                            {exam}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#3d3d3d]" />
                    </div>
                  </div>

                  <div>
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      {t("usNurses.form.examTimeline")} *
                    </label>
                    <div className="relative">
                      <select
                        name="examTimeline"
                        value={formData.examTimeline}
                        onChange={handleInputChange}
                        required
                        className={selectClass}
                      >
                        <option value="">
                          {t("usNurses.form.selectTimeline")}
                        </option>
                        {timelineOptions.map((timeline) => (
                          <option key={timeline} value={timeline}>
                            {timeline}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#3d3d3d]" />
                    </div>
                  </div>

                  <div>
                    <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                      {t("usNurses.form.languageLevel")} *
                    </label>
                    <div className="relative">
                      <select
                        name="languageLevel"
                        value={formData.languageLevel}
                        onChange={handleInputChange}
                        required
                        className={selectClass}
                      >
                        <option value="">
                          {t("usNurses.form.selectLevel")}
                        </option>
                        {levelOptions.map((level) => (
                          <option key={level.value} value={level.value}>
                            {level.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#3d3d3d]" />
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                    {t("usNurses.form.additionalInfo")}
                  </label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full resize-none rounded-xl border-2 border-[#14B82C]/30 px-4 py-3 text-[#042F0C] transition-all focus:border-[#14B82C] focus:outline-none focus:ring-2 focus:ring-[#14B82C]/20"
                    placeholder={t("usNurses.form.additionalInfoPlaceholder")}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex transform items-center gap-3 rounded-full px-12 py-4 text-xl font-bold transition-all hover:scale-105 ${
                    isSubmitting
                      ? "cursor-not-allowed bg-gray-400 text-gray-600"
                      : "bg-gradient-to-r from-[#14B82C] to-[#0F9A1A] text-white shadow-lg hover:from-[#0F9A1A] hover:to-[#0D7A15] hover:shadow-xl"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      {t("usNurses.form.submitting")}
                    </>
                  ) : (
                    <>
                      <Send className="h-6 w-6" />
                      {t("usNurses.form.submitButton")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default USNursesInterestForm;
